import React, { useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import api from "../../lib/axios"; // Import your configured axios instance

const Workflow = () => {
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [uploadStatus, setUploadStatus] = useState({});
  const [loading, setLoading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedScript, setSelectedScript] = useState("gene_hunt");
  const cancelTokens = useRef({}); // Store cancel tokens for each file
  
  const scripts = [
    { value: "gene_hunt", name: "Gene Hunt" },
    // { value: "variant_analysis", name: "Variant Analysis" },
    // Add more scripts as needed
  ];

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...newFiles]);
    setUploadProgress({});
    setUploadStatus({});
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
    setUploadProgress({});
    setUploadStatus({});
  };

  const clearFiles = () => {
    setFiles([]);
    setUploadProgress({});
    setUploadStatus({});
  };

  const uploadFile = async (file) => {
    // Create cancel token for this file
    const cancelTokenSource = axios.CancelToken.source();
    cancelTokens.current[file.name] = cancelTokenSource;

    try {
      const resp = await api.post("/v2/upload", { file_key: file.name });
      const signedUrl = resp.data.data?.signed_url;
      const fileKey = resp.data.data?.key;
      
      await api.put(signedUrl, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (evt) => {
          const percent = Math.round((evt.loaded * 100) / evt.total);
          setUploadProgress((prev) => ({ ...prev, [file.name]: percent }));
        },
        cancelToken: cancelTokenSource.token
      });
      
      setUploadStatus((prev) => ({ ...prev, [file.name]: "success" }));
      return fileKey;
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log(`Upload cancelled for ${file.name}`);
        setUploadStatus((prev) => ({ ...prev, [file.name]: "cancelled" }));
      } else {
        console.error(err);
        setUploadStatus((prev) => ({ ...prev, [file.name]: "error" }));
      }
      return null;
    } finally {
      // Clean up the cancel token
      delete cancelTokens.current[file.name];
    }
  };

  const cancelUpload = (fileName) => {
    if (cancelTokens.current[fileName]) {
      cancelTokens.current[fileName].cancel(`Upload cancelled by user for ${fileName}`);
    }
  };

  const createTask = async (fileKey, fileName) => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user?.email) return null;
      
      const resp = await api.post("/v2/tasks", {
        name: fileName,                  // Use file name as task name
        file: fileKey,                   // File key from upload
        description: `Task for ${fileName}`,  // Basic description
        email: user.email,               // User's email
        script: selectedScript           // Selected script from dropdown
      });
      return resp.data.data;
    } catch (err) {
      console.error('Error creating task:', err);
      return null;
    }
  };

  const runTask = async (taskId, workflowId) => {
    try {
      // First update the workflow with the new task ID
      await api.put(`/v2/workflows/${workflowId}`, {
        tasks: [taskId],
        update_type: 'append'  // Assuming your API supports this to append to existing tasks
      });
      
      // Then start the task
      await api.post(`/v2/tasks/${taskId}/run`, { workflow_id: workflowId });
      return true;
    } catch (err) {
      console.error('Error in runTask:', err);
      return false;
    }
  };

  const handleTasksCreation = async () => {
    setLoading(true);
    
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      if (!user.email) return navigate("/sign_in");
      
      // Create a new workflow
      const workflowResp = await api.post("/v2/workflows", {
        email: user.email,
        tasks: [], // Start with no tasks
      });
      const workflowId = workflowResp.data.data?.workflow_id;
      
      if (!workflowId) {
        throw new Error('Failed to create workflow');
      }

      // Process each file independently
      const processFile = async (file) => {
        let taskId;
        try {
          setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
          
          // Upload the file
          const fileKey = await uploadFile(file);
          if (!fileKey) throw new Error('Upload failed');
          
          // Create task for the uploaded file
          const task = await createTask(fileKey, file.name);
          if (!task?.task_id) throw new Error('Task creation failed');
          
          taskId = task.task_id;
          
          // Run the task (which will also update the workflow with this task)
          await runTask(taskId, workflowId);
          
          // Update status to completed
          setUploadStatus(prev => ({ ...prev, [file.name]: 'completed' }));
          return taskId;
        } catch (error) {
          console.error(`Error processing file ${file.name}:`, error);
          setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
          return null;
        }
      };
      
      // Process all files in parallel
      const taskIds = (await Promise.all(files.map(processFile))).filter(Boolean);
      
      if (taskIds.length > 0) {
        try {
          // Update workflow with the task IDs
          await api.put(`/v2/workflows/${workflowId}`, {
            tasks: taskIds
          });
          
          // Navigate to workflow page after successful update
          navigate(`/v2/workflow/${workflowId}`);
        } catch (error) {
          console.error('Error updating workflow with tasks:', error);
          throw new Error('Failed to update workflow with tasks');
        }
      } else {
        throw new Error('No files were processed successfully');
      }
      
    } catch (error) {
      console.error('Error in workflow creation:', error);
      // Optionally show error to user
    } finally {
      setLoading(false);
    }
  };



  return (
    <div className="h-full flex flex-col items-center justify-center bg-gray-50 w-full gap-10 p-16">
      <div className="flex items-center justify-center">
        <h1 className="text-2xl font-bold">Select Workflow</h1>
      </div>
      <div className="flex flex-col items-center justify-center gap-3">
        {/* add a select field to select the script */}
        <select
          value={selectedScript}
          onChange={(e) => setSelectedScript(e.target.value)}
          className="bg-white text-slate-500 px-2 py-1 text-base rounded max-w-md flex items-center justify-center cursor-pointer border-2 border-gray-300 mx-auto"
        >
          {scripts.map((script) => (
            <option key={script.value} value={script.value}>
              {script.name}
            </option>
          ))}
        </select>
        <label
          htmlFor="uploadFile1"
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          className={`
          bg-white text-slate-500 p-2 font-semibold text-base rounded max-w-md h-52 flex flex-col items-center justify-center cursor-pointer border-2 border-gray-300 border-dashed mx-auto 
          w-96
          ${isDragging ? "border-blue-500 bg-blue-50" : ""}
          `}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="w-11 mb-3 fill-gray-500"
            viewBox="0 0 32 32"
          >
            <path d="M23.75 11.044a7.99 7.99 0 0 0-15.5-.009A8 8 0 0 0 9 27h3a1 1 0 0 0 0-2H9a6 6 0 0 1-.035-12 1.038 1.038 0 0 0 1.1-.854 5.991 5.991 0 0 1 11.862 0A1.08 1.08 0 0 0 23 13a6 6 0 0 1 0 12h-3a1 1 0 0 0 0 2h3a8 8 0 0 0 .75-15.956z" />
            <path d="M20.293 19.707a1 1 0 0 0 1.414-1.414l-5-5a1 1 0 0 0-1.414 0l-5 5a1 1 0 0 0 1.414 1.414L15 16.414V29a1 1 0 0 0 2 0V16.414z" />
          </svg>
          <span>Upload file</span>
          <input
            type="file"
            id="uploadFile1"
            className="hidden"
            multiple
            onChange={handleFileChange}
          />
          <p className="text-xs font-medium text-slate-400 mt-2">
            Click or Drag and Drop to upload
          </p>
        </label>

        <div className=" flex gap-5">
          <button
            onClick={handleTasksCreation}
            disabled={!files.length || loading}
            className={`
        px-4 py-2 text-white rounded 
        ${
          !files.length || loading
            ? "bg-blue-300 cursor-not-allowed"
            : "bg-blue-600 cursor-pointer"
        }
        `}
          >
            {loading ? "Uploading..." : "Upload"}
          </button>
          <button
            onClick={clearFiles}
            disabled={!files.length || loading}
            className={`
      ml-2 px-4 py-2 text-white rounded
      ${
        !files.length || loading
          ? "bg-red-600/60 cursor-not-allowed"
          : "bg-red-600 cursor-pointer"
      }
        `}
          >
            Clear
          </button>
        </div>
      </div>
      <div className="mt-4 w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
        {files.map((file) => (
          <div
            key={file.name}
            className="bg-white p-3 rounded border shadow-sm relative"
          >
            <div className="flex justify-between items-start">
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{file.name}</div>
                <div className="text-xs text-gray-500">
                  {file.size > 1024 * 1024 
                    ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
                    : `${Math.round(file.size / 1024)} KB`}
                </div>
                <div className="mt-1">
                  {uploadStatus[file.name] === "uploading" && (
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${uploadProgress[file.name] || 0}%` }}
                      ></div>
                    </div>
                  )}
                </div>
              </div>
              
              {uploadStatus[file.name] === "uploading" && (
                <button
                  onClick={() => cancelUpload(file.name)}
                  className="ml-2 px-2 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200 transition-colors"
                  title="Cancel upload"
                >
                  Cancel
                </button>
              )}
              {uploadStatus[file.name] === "success" && (
                <span className="text-green-500 ml-2">✅</span>
              )}
              {uploadStatus[file.name] === "error" && (
                <span className="text-red-500 ml-2">❌</span>
              )}
              {uploadStatus[file.name] === "cancelled" && (
                <span className="text-gray-500 ml-2 text-sm">Cancelled</span>
              )}
            </div>
            
            {uploadStatus[file.name] === "uploading" && (
              <div className="text-xs text-gray-500 mt-1">
                {uploadProgress[file.name] || 0}% uploaded
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default Workflow;
